import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GeoLocationFilters } from "../components/GeoLocationFilters";
import { LeadsImportPanel } from "../components/LeadsImportPanel";
import { PageHeader } from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import {
  fetchLeadsTable,
  updateLeadHoStatus,
  type LeadHoAction,
  type LeadTableRow,
} from "../api/leadsTableApi";
import { useGeoDirectory } from "../context/GeoDirectoryContext";
import {
  leadHoStatusKind,
  leadHoStatusMatchesFilter,
  leadHoStatusShortLabel,
  leadRowMatchesGeoFilter,
  leadSheetInterestDisplayLabel,
  leadSheetInterestKind,
  leadSheetInterestMatchesFilter,
  leadSheetStatusDisplayLabel,
  type LeadHoStatusFilter,
  type LeadSheetInterestFilter,
} from "../utils/leadTableRowFilter";
import { toDisplayCamelCase } from "../utils/textCase";

const LEAD_HO_STATUS_FILTER_OPTIONS: { value: LeadHoStatusFilter; label: string }[] = [
  { value: "all", label: "All HO statuses" },
  { value: "pending", label: "Interested — HO not set" },
  { value: "approved", label: "Approve" },
  { value: "reject", label: "Reject" },
  { value: "on_hold", label: "On hold" },
];

const LEAD_LIST_STATUS_FILTER_OPTIONS: { value: LeadSheetInterestFilter; label: string }[] = [
  { value: "all", label: "All list statuses" },
  { value: "pending", label: "Pending / other" },
  { value: "interested", label: "Interested" },
  { value: "not_interested", label: "Not interested" },
];

function LeadsStatusCell({ row }: { row: LeadTableRow }) {
  const hoKind = leadHoStatusKind(row.hoStatus);
  const sheetKind = leadSheetInterestKind(row.status);
  const sheetLabel = leadSheetInterestDisplayLabel(row.status);

  return (
    <div className="lead-status-cell">
      {hoKind !== "none" ? (
        <span className={`lead-status-pill lead-status-pill--${hoKind}`}>{leadHoStatusShortLabel(row.hoStatus)}</span>
      ) : null}
      {sheetKind === "interested" ? (
        <span className="lead-status-pill lead-status-pill--interested">{sheetLabel}</span>
      ) : sheetKind === "not_interested" ? (
        <span className="lead-status-pill lead-status-pill--not-interested">{sheetLabel}</span>
      ) : (
        <span className="text-muted">{sheetLabel}</span>
      )}
    </div>
  );
}

export function LeadsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = useAuth();
  const { store: geoStore } = useGeoDirectory();
  const { showToast } = useToast();
  const importQuery = searchParams.get("import");
  const canViewLeadsTable = can("leads");
  /** Import is a separate permission from viewing the table (`leads` vs `leads_import`). */
  const canImportLeads = can("leads_import");
  const importOnly = canImportLeads && !canViewLeadsTable;

  const [search, setSearch] = useState("");
  const [hoStatusFilter, setHoStatusFilter] = useState<LeadHoStatusFilter>("all");
  const [listStatusFilter, setListStatusFilter] = useState<LeadSheetInterestFilter>("all");
  const [districtFilter, setDistrictFilter] = useState("");
  const [mandalFilter, setMandalFilter] = useState("");
  const [villageFilter, setVillageFilter] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const importOnlyAutoOpenedRef = useRef(false);
  const importSectionRef = useRef<HTMLDivElement>(null);

  const [rows, setRows] = useState<LeadTableRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(canViewLeadsTable);
  /** Lead IDs whose Action buttons are currently saving. */
  const [savingLeadIds, setSavingLeadIds] = useState<Set<string>>(new Set());

  const setLeadHoStatusAction = useCallback(
    async (leadId: string, next: LeadHoAction) => {
      setSavingLeadIds((prev) => {
        const n = new Set(prev);
        n.add(leadId);
        return n;
      });
      const previousRow = rows.find((r) => r.leadId === leadId);
      setRows((prev) =>
        prev.map((r) =>
          r.leadId === leadId
            ? { ...r, hoStatus: next === "" ? null : next }
            : r,
        ),
      );
      try {
        const updated = await updateLeadHoStatus(leadId, next);
        setRows((prev) => prev.map((r) => (r.leadId === leadId ? updated : r)));
      } catch (e) {
        if (previousRow) {
          setRows((prev) => prev.map((r) => (r.leadId === leadId ? previousRow : r)));
        }
        showToast(e instanceof Error ? e.message : "Could not update HO status.", "error");
      } finally {
        setSavingLeadIds((prev) => {
          const n = new Set(prev);
          n.delete(leadId);
          return n;
        });
      }
    },
    [rows, showToast],
  );

  const reloadLeads = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchLeadsTable();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setRows([]);
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canViewLeadsTable) {
      setLoading(false);
      setRows([]);
      setLoadError(null);
      return;
    }
    void reloadLeads();
  }, [canViewLeadsTable, reloadLeads]);

  useEffect(() => {
    if (importQuery === "1" && !canImportLeads) {
      setImportOpen(false);
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.delete("import");
          return p;
        },
        { replace: true },
      );
      return;
    }
    if (importQuery === "1" && canImportLeads) {
      setImportOpen(true);
      return;
    }
    if (importOnly && !importOnlyAutoOpenedRef.current) {
      setImportOpen(true);
      importOnlyAutoOpenedRef.current = true;
    }
  }, [importQuery, importOnly, canImportLeads, setSearchParams]);

  useEffect(() => {
    if (!canImportLeads && importOpen) setImportOpen(false);
  }, [canImportLeads, importOpen]);

  useEffect(() => {
    if (importOpen && importQuery === "1") {
      importSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [importOpen, importQuery]);

  function openImportPanel() {
    if (!canImportLeads) return;
    setImportOpen(true);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("import", "1");
        return p;
      },
      { replace: true },
    );
  }

  function closeImportPanel() {
    setImportOpen(false);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete("import");
        return p;
      },
      { replace: true },
    );
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q) {
        const hay = [
          r.leadId,
          r.applicantName,
          r.mobileNumber,
          r.aadhaarNumber,
          r.mandal,
          r.village,
          r.district,
          r.status,
          r.hoStatus,
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (!leadHoStatusMatchesFilter(r, hoStatusFilter)) return false;
      if (!leadSheetInterestMatchesFilter(r, listStatusFilter)) return false;
      if (!leadRowMatchesGeoFilter(r, { district: districtFilter, mandal: mandalFilter, village: villageFilter }, geoStore)) {
        return false;
      }
      return true;
    });
  }, [rows, search, hoStatusFilter, listStatusFilter, districtFilter, mandalFilter, villageFilter, geoStore]);

  const hasActiveFilters =
    search.trim() !== "" ||
    hoStatusFilter !== "all" ||
    listStatusFilter !== "all" ||
    districtFilter !== "" ||
    mandalFilter !== "" ||
    villageFilter !== "";

  function clearFilters() {
    setSearch("");
    setHoStatusFilter("all");
    setListStatusFilter("all");
    setDistrictFilter("");
    setMandalFilter("");
    setVillageFilter("");
  }

  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: importOnly ? "Leads import" : "Leads" },
        ]}
        title={importOnly ? "Leads import" : "Leads"}
        actions={
          canImportLeads ? (
            <div className="leads-import-header-actions">
              {!importOpen ? (
                <div className="leads-import-box leads-import-box--corner card card--gov card--elevated">
                  <button type="button" className="btn btn--secondary btn--tg" onClick={() => openImportPanel()}>
                    Import leads (PDF or Excel)
                  </button>
                </div>
              ) : (
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => closeImportPanel()}>
                  Close import
                </button>
              )}
            </div>
          ) : null
        }
      />

      {importOpen && canImportLeads ? (
        <div id="leads-import-section" ref={importSectionRef} className="leads-import-panel-below-header">
          <LeadsImportPanel
            onSaved={() => {
              if (canViewLeadsTable) void reloadLeads();
            }}
          />
        </div>
      ) : null}

      {importOnly && !importOpen ? (
        <p className="section-lead" style={{ marginBottom: "1rem" }}>
          Your account can import leads only. Use <strong>Import leads (PDF or Excel)</strong> above to upload a file.
        </p>
      ) : null}

      {canViewLeadsTable && loadError ? (
        <p className="text-muted" style={{ color: "var(--dash-danger, #b91c1c)", marginBottom: "1rem" }}>
          Could not load the Leads table: {loadError}
        </p>
      ) : null}

      {canViewLeadsTable ? (
      <div className="card card--gov card--elevated" style={{ marginBottom: "1rem" }}>
        <div className="table-toolbar table-toolbar--stack">
          <div className="table-toolbar__top">
            <span className="table-toolbar__count">
              {loading
                ? "Loading leads…"
                : hasActiveFilters
                  ? `${filtered.length} of ${rows.length} lead(s)`
                  : `${rows.length} lead(s)`}
            </span>
            {hasActiveFilters ? (
              <button type="button" className="btn btn--link btn--sm table-toolbar__clear" onClick={clearFilters}>
                Clear filters
              </button>
            ) : null}
          </div>
          <div className="table-filters-grid">
            <div className="table-filters table-filters--leads" role="search" aria-label="Filter leads">
              <div className="field table-filters__field table-filters__field--grow">
                <label htmlFor="leads-list-search">Search</label>
                <input
                  id="leads-list-search"
                  type="search"
                  placeholder="Application no., name, village…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="field table-filters__field">
                <label htmlFor="leads-filter-status">HO status</label>
                <select
                  id="leads-filter-ho-status"
                  value={hoStatusFilter}
                  onChange={(e) => setHoStatusFilter(e.target.value as LeadHoStatusFilter)}
                >
                  {LEAD_HO_STATUS_FILTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field table-filters__field">
                <label htmlFor="leads-filter-list-status">List status</label>
                <select
                  id="leads-filter-list-status"
                  value={listStatusFilter}
                  onChange={(e) => setListStatusFilter(e.target.value as LeadSheetInterestFilter)}
                >
                  {LEAD_LIST_STATUS_FILTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <GeoLocationFilters
                idPrefix="leads-geo"
                district={districtFilter}
                mandal={mandalFilter}
                village={villageFilter}
                onDistrictChange={(d) => {
                  setDistrictFilter(d);
                  setMandalFilter("");
                  setVillageFilter("");
                }}
                onMandalChange={(m) => {
                  setMandalFilter(m);
                  setVillageFilter("");
                }}
                onVillageChange={setVillageFilter}
              />
            </div>
          </div>
        </div>

        <div className="table-wrap" style={{ marginTop: "1rem" }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Application No.</th>
                <th>Applicant Name</th>
                <th>District</th>
                <th>Mandal</th>
                <th>Village</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="data-table__empty">
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="data-table__empty">
                    {rows.length === 0
                      ? "No rows in the Leads table yet. Use Import leads (top right) to save from PDF or Excel."
                      : "No leads match the current filters."}
                  </td>
                </tr>
              ) : (
                filtered.map((r) => {
                  const hoKind = leadHoStatusKind(r.hoStatus);
                  const saving = savingLeadIds.has(r.leadId);
                  return (
                    <tr key={r.leadId}>
                      <td className="mono">{r.leadId}</td>
                      <td>
                        <span className="table-name">{toDisplayCamelCase(r.applicantName) || "—"}</span>
                      </td>
                      <td>{toDisplayCamelCase(r.district) || "—"}</td>
                      <td>{toDisplayCamelCase(r.mandal) || "—"}</td>
                      <td>{toDisplayCamelCase(r.village) || "—"}</td>
                      <td>
                        <LeadsStatusCell row={r} />
                      </td>
                      <td className="lead-action-cell">
                        <select
                          className="lead-action-select"
                          aria-label={`HO action for application ${r.leadId}`}
                          value={hoKind === "none" ? "" : hoKind}
                          disabled={saving}
                          aria-busy={saving}
                          onChange={(e) => {
                            const v = e.target.value;
                            void setLeadHoStatusAction(r.leadId, v === "" ? "" : (v as LeadHoAction));
                          }}
                        >
                          <option value="">Choose HO action…</option>
                          <option value="approved">Approve</option>
                          <option value="reject">Reject</option>
                          <option value="on_hold">On hold</option>
                        </select>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      ) : null}
    </>
  );
}
